const CropperModule = require('cropperjs');
const Cropper = CropperModule.default || CropperModule.Cropper || CropperModule;

const CROP_ASPECT_RATIO = 1;
const BOUNDARY_TOLERANCE = 0.5;
const CROPPER_TEMPLATE = '<cropper-canvas background>'
    + '<cropper-image scalable translatable></cropper-image>'
    + '<cropper-handle action="move" plain></cropper-handle>'
    + '<cropper-selection movable resizable>'
    + '<cropper-grid role="grid" bordered covered></cropper-grid>'
    + '<cropper-crosshair centered></cropper-crosshair>'
    + '<cropper-handle action="move" theme-color="rgba(255, 255, 255, 0.35)"></cropper-handle>'
    + '<cropper-handle action="n-resize"></cropper-handle>'
    + '<cropper-handle action="e-resize"></cropper-handle>'
    + '<cropper-handle action="s-resize"></cropper-handle>'
    + '<cropper-handle action="w-resize"></cropper-handle>'
    + '<cropper-handle action="ne-resize"></cropper-handle>'
    + '<cropper-handle action="nw-resize"></cropper-handle>'
    + '<cropper-handle action="se-resize"></cropper-handle>'
    + '<cropper-handle action="sw-resize"></cropper-handle>'
    + '</cropper-selection>'
    + '</cropper-canvas>';

class EntryImageCropper {
    constructor(image) {
        this.cropper = new Cropper(image, {template: CROPPER_TEMPLATE});
        this.cropperCanvas = this.cropper.getCropperCanvas();
        this.cropperImage = this.cropper.getCropperImage();
        this.cropperSelection = this.cropper.getCropperSelection();
        this.onSelectionChange = this.handleSelectionChange.bind(this);
        this.onImageTransform = this.handleImageTransform.bind(this);
        this.isAdjustingSelection = false;

        if (this.cropperSelection !== null) {
            this.cropperSelection.aspectRatio = CROP_ASPECT_RATIO;
            this.cropperSelection.initialAspectRatio = CROP_ASPECT_RATIO;
        }
    }

    ready() {
        if (this.cropperCanvas === null || this.cropperImage === null || this.cropperSelection === null) {
            return Promise.reject(new Error('Failed to initialize image cropper.'));
        }

        return this.cropperImage.$ready()
            .then(() => new Promise((resolve) => window.requestAnimationFrame(resolve)))
            .then(() => {
                this.initializeSelection();
                $(this.cropperSelection).on('change.entryImageCropper', this.onSelectionChange);
                $(this.cropperImage).on('transform.entryImageCropper', this.onImageTransform);
            });
    }

    initializeSelection() {
        const imageBounds = this.getImageBounds(this.cropperImage.$getTransform());
        const left = Math.max(0, imageBounds.left);
        const top = Math.max(0, imageBounds.top);
        const right = Math.min(this.cropperCanvas.clientWidth, imageBounds.right);
        const bottom = Math.min(this.cropperCanvas.clientHeight, imageBounds.bottom);
        const width = right - left;
        const height = bottom - top;
        const size = Math.min(width, height);

        if (size <= 0) {
            throw new Error('Failed to calculate the initial crop selection.');
        }

        this.cropperSelection.$change(
            left + ((width - size) / 2),
            top + ((height - size) / 2),
            size,
            size,
            CROP_ASPECT_RATIO
        );
    }

    getImageBounds(matrix) {
        const [scaleX, skewY, skewX, scaleY, translateX, translateY] = matrix;

        if (skewX !== 0 || skewY !== 0 || scaleX <= 0 || scaleY <= 0) {
            return {left: Infinity, top: Infinity, right: -Infinity, bottom: -Infinity};
        }

        const width = this.cropperImage.offsetWidth;
        const height = this.cropperImage.offsetHeight;
        const left = this.cropperImage.offsetLeft + ((1 - scaleX) * width / 2) + translateX;
        const top = this.cropperImage.offsetTop + ((1 - scaleY) * height / 2) + translateY;

        return {
            left,
            top,
            right: left + (width * scaleX),
            bottom: top + (height * scaleY)
        };
    }

    isSelectionInsideImage(selection, imageBounds) {
        return selection.x >= imageBounds.left - BOUNDARY_TOLERANCE
            && selection.y >= imageBounds.top - BOUNDARY_TOLERANCE
            && selection.x + selection.width <= imageBounds.right + BOUNDARY_TOLERANCE
            && selection.y + selection.height <= imageBounds.bottom + BOUNDARY_TOLERANCE;
    }

    getSelectionInsideImage(selection, imageBounds) {
        const size = Math.min(
            selection.width,
            selection.height,
            imageBounds.right - imageBounds.left,
            imageBounds.bottom - imageBounds.top
        );

        return {
            x: Math.min(Math.max(selection.x, imageBounds.left), imageBounds.right - size),
            y: Math.min(Math.max(selection.y, imageBounds.top), imageBounds.bottom - size),
            width: size,
            height: size
        };
    }

    handleSelectionChange(event) {
        if (this.isAdjustingSelection) {
            return;
        }

        const cropperEvent = event.originalEvent || event;
        const imageBounds = this.getImageBounds(this.cropperImage.$getTransform());

        if (!this.isSelectionInsideImage(cropperEvent.detail, imageBounds)) {
            event.preventDefault();
            const adjustedSelection = this.getSelectionInsideImage(cropperEvent.detail, imageBounds);
            this.isAdjustingSelection = true;
            this.cropperSelection.$change(
                adjustedSelection.x,
                adjustedSelection.y,
                adjustedSelection.width,
                adjustedSelection.height,
                CROP_ASPECT_RATIO
            );
            this.isAdjustingSelection = false;
        }
    }

    handleImageTransform(event) {
        const selection = {
            x: this.cropperSelection.x,
            y: this.cropperSelection.y,
            width: this.cropperSelection.width,
            height: this.cropperSelection.height
        };
        const cropperEvent = event.originalEvent || event;
        const imageBounds = this.getImageBounds(cropperEvent.detail.matrix);

        if (!this.isSelectionInsideImage(selection, imageBounds)) {
            event.preventDefault();
        }
    }

    toBlob(width, height, mimeType, quality) {
        if (this.cropperSelection === null) {
            return Promise.reject(new Error('Crop selection is not available.'));
        }

        return this.cropperSelection.$toCanvas({width, height}).then((canvas) => new Promise((resolve, reject) => {
            canvas.toBlob((blob) => {
                if (blob === null) {
                    reject(new Error('Failed to create cropped image.'));
                    return;
                }
                resolve(blob);
            }, mimeType, quality);
        }));
    }

    destroy() {
        if (this.cropperSelection !== null) {
            $(this.cropperSelection).off('change.entryImageCropper', this.onSelectionChange);
        }
        if (this.cropperImage !== null) {
            $(this.cropperImage).off('transform.entryImageCropper', this.onImageTransform);
        }
        if (this.cropperCanvas !== null) {
            this.cropperCanvas.remove();
        }
        this.cropperCanvas = null;
        this.cropperImage = null;
        this.cropperSelection = null;
    }
}

global.EntryImageCropper = EntryImageCropper;
